import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  Users,
  ShieldAlert,
  Clock,
  ExternalLink,
  X,
  UserCheck,
  FileWarning,
} from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { formatDateOnly } from '@/lib/date-utils';
import {
  useSupervisionCockpit,
} from '@/hooks/use-staffing-board';
import type {
  SupervisionCockpitData,
  SupervisionMissingL3,
  SupervisionCertExpiring,
  SupervisionCertExpired,
  SupervisionNominatedPersonInvalid,
  SupervisionCorrectiveAction,
  SupervisionPendingSafetyApproval,
} from '@/hooks/use-staffing-board';

// ── Types ────────────────────────────────────────────────────────────────────

interface SupervisionCockpitPanelProps {
  onClose: () => void;
}

type QueueTab =
  | 'missing_l3'
  | 'cert_expiring'
  | 'cert_expired'
  | 'nominated_person_invalid'
  | 'open_corrective_actions'
  | 'pending_safety_approvals';

interface TabDef {
  key: QueueTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const TABS: TabDef[] = [
  { key: 'missing_l3', label: 'Missing L3', icon: Users, color: 'text-orange-600' },
  { key: 'cert_expiring', label: 'Expiring Certs', icon: Clock, color: 'text-amber-600' },
  { key: 'cert_expired', label: 'Expired Certs', icon: AlertTriangle, color: 'text-red-600' },
  { key: 'nominated_person_invalid', label: 'NP Cert Gap', icon: UserCheck, color: 'text-red-600' },
  { key: 'open_corrective_actions', label: 'CAPA', icon: FileWarning, color: 'text-purple-600' },
  { key: 'pending_safety_approvals', label: 'Alerts', icon: ShieldAlert, color: 'text-blue-600' },
];

// ── Component ────────────────────────────────────────────────────────────────

export function SupervisionCockpitPanel({ onClose }: SupervisionCockpitPanelProps) {
  const { data, isLoading } = useSupervisionCockpit(true);
  const [activeTab, setActiveTab] = useState<QueueTab>('missing_l3');

  const summary = data?.summary;
  const queues = data?.queues;
  const crewEnabled = data?.modules?.crew ?? true;
  const complianceEnabled = data?.modules?.compliance ?? true;
  // Without H3Comply access the compliance queues report only whether
  // something is waiting (owner ruling, SEC-S9 follow-up).
  const complianceRestricted = data?.restricted?.compliance ?? false;
  const flags = data?.flags;

  // Filter tabs based on module availability
  const CREW_TABS: QueueTab[] = ['missing_l3', 'cert_expiring', 'cert_expired'];
  const COMPLIANCE_TABS: QueueTab[] = ['nominated_person_invalid', 'open_corrective_actions', 'pending_safety_approvals'];

  const visibleTabs = useMemo(() =>
    TABS.filter((tab) => {
      if (CREW_TABS.includes(tab.key)) return crewEnabled;
      if (COMPLIANCE_TABS.includes(tab.key)) return complianceEnabled;
      return true;
    }),
  [crewEnabled, complianceEnabled]);

  // Reset active tab if current is hidden
  const validActiveTab = visibleTabs.some((t) => t.key === activeTab) ? activeTab : visibleTabs[0]?.key ?? 'missing_l3';

  const tabCounts: Record<QueueTab, number> = useMemo(() => ({
    missing_l3: summary?.missing_l3_count ?? 0,
    cert_expiring: summary?.cert_expiring_count ?? 0,
    cert_expired: summary?.cert_expired_count ?? 0,
    nominated_person_invalid: summary?.nominated_person_invalid_count ?? 0,
    open_corrective_actions: summary?.open_corrective_actions_count ?? 0,
    pending_safety_approvals: summary?.pending_safety_approvals_count ?? 0,
  }), [summary]);

  const totalIssues = useMemo(() =>
    visibleTabs.reduce((sum, tab) => sum + (tabCounts[tab.key] ?? 0), 0),
  [tabCounts, visibleTabs]);
  // Restricted queues add no number to the total, only an alert.
  const hasRestrictedFlags = complianceRestricted
    && visibleTabs.some((tab) => isFlaggedComplianceTab(tab.key, flags));

  return (
    <div className="w-80 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white flex flex-col print:hidden" data-testid="supervision-cockpit-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">Supervision</h3>
          {(totalIssues > 0 || hasRestrictedFlags) && (
            <span
              className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-100 text-xs font-medium text-red-700"
              data-testid="supervision-total"
            >
              {totalIssues > 0 ? totalIssues : '!'}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          aria-label="Close supervision panel"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      )}

      {/* Tabs */}
      {!isLoading && data && (
        <>
          <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-gray-100">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors',
                  validActiveTab === tab.key
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                    : 'text-gray-600 hover:bg-gray-100',
                )}
                data-testid={`supervision-tab-${tab.key}`}
              >
                <tab.icon className={cn('h-3 w-3', tab.color)} />
                {tab.label}
                {(tabCounts[tab.key] > 0 || (complianceRestricted && isFlaggedComplianceTab(tab.key, flags))) && (
                  <span className={cn(
                    'ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-semibold',
                    validActiveTab === tab.key
                      ? 'bg-indigo-200 text-indigo-800'
                      : 'bg-gray-200 text-gray-700',
                  )}>
                    {tabCounts[tab.key] > 0 ? tabCounts[tab.key] : '!'}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Queue content */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {validActiveTab === 'missing_l3' && (
              <QueueList
                items={queues?.missing_l3 ?? []}
                emptyText="All active projects have L3 coverage."
                renderItem={(item: SupervisionMissingL3) => (
                  <QueueItem
                    key={item.project_id}
                    title={item.project_name}
                    subtitle={item.highest_level === 0 ? 'No certified techs' : `Highest: Level ${item.highest_level}`}
                    severity="warning"
                    link={item.link}
                  />
                )}
              />
            )}
            {validActiveTab === 'cert_expiring' && (
              <QueueList
                items={queues?.cert_expiring ?? []}
                emptyText="No certifications expiring within 60 days."
                renderItem={(item: SupervisionCertExpiring) => (
                  <QueueItem
                    key={`${item.tech_id}-${item.cert_type ?? 'flag'}`}
                    title={item.tech_name}
                    subtitle={item.cert_name
                      ? `${item.cert_name} — ${item.days_until_expiry}d left`
                      : 'Certification expiring soon'}
                    severity="warning"
                    link={item.link}
                  />
                )}
              />
            )}
            {validActiveTab === 'cert_expired' && (
              <QueueList
                items={queues?.cert_expired ?? []}
                emptyText="No expired certifications on active projects."
                renderItem={(item: SupervisionCertExpired) => (
                  <QueueItem
                    key={`${item.tech_id}-${item.cert_type ?? 'flag'}`}
                    title={item.tech_name}
                    subtitle={item.cert_name
                      ? `${item.cert_name} — expired ${formatDateOnly(item.expiry_date, 'Unknown')}`
                      : 'Certification expired'}
                    severity="critical"
                    link={item.link}
                  />
                )}
              />
            )}
            {complianceRestricted && isComplianceTab(validActiveTab) && (
              <RestrictedQueue flagged={isFlaggedComplianceTab(validActiveTab, flags)} />
            )}
            {!complianceRestricted && validActiveTab === 'nominated_person_invalid' && (
              <QueueList
                items={queues?.nominated_person_invalid ?? []}
                emptyText="All Nominated Persons have valid certifications."
                renderItem={(item: SupervisionNominatedPersonInvalid) => (
                  <QueueItem
                    key={item.id}
                    title={item.name}
                    subtitle={`${item.role_title} — needs ${item.required_cert_level.toUpperCase()}`}
                    severity="critical"
                    link={item.link}
                  />
                )}
              />
            )}
            {!complianceRestricted && validActiveTab === 'open_corrective_actions' && (
              <QueueList
                items={queues?.open_corrective_actions ?? []}
                emptyText="No open corrective actions."
                renderItem={(item: SupervisionCorrectiveAction) => (
                  <QueueItem
                    key={item.id}
                    title={item.title}
                    subtitle={`${item.status}${item.is_overdue ? ' (OVERDUE)' : ''} — due ${formatDateOnly(item.due_date, 'N/A')}${item.assigned_to ? ` → ${item.assigned_to}` : ''}`}
                    severity={item.is_overdue ? 'critical' : 'warning'}
                    link={item.link}
                  />
                )}
              />
            )}
            {!complianceRestricted && validActiveTab === 'pending_safety_approvals' && (
              <QueueList
                items={queues?.pending_safety_approvals ?? []}
                emptyText="No pending compliance alerts."
                renderItem={(item: SupervisionPendingSafetyApproval) => (
                  <QueueItem
                    key={item.id}
                    title={item.title}
                    subtitle={`${item.alert_type.replace(/_/g, ' ')} — ${item.severity}`}
                    severity={item.severity === 'critical' ? 'critical' : 'info'}
                    link={item.link}
                  />
                )}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

type ComplianceTab = 'nominated_person_invalid' | 'open_corrective_actions' | 'pending_safety_approvals';

function isComplianceTab(tab: QueueTab): tab is ComplianceTab {
  return (
    tab === 'nominated_person_invalid'
    || tab === 'open_corrective_actions'
    || tab === 'pending_safety_approvals'
  );
}

function isFlaggedComplianceTab(
  tab: QueueTab,
  flags: SupervisionCockpitData['flags'],
): boolean {
  return isComplianceTab(tab) && Boolean(flags?.[tab]);
}

function RestrictedQueue({ flagged }: { flagged: boolean }) {
  return (
    <p className="text-xs text-gray-500 text-center py-4" data-testid="supervision-restricted">
      {flagged
        ? 'Items are waiting here. You need H3Comply access to see them.'
        : 'Nothing waiting.'}
    </p>
  );
}

function QueueList<T>({
  items,
  emptyText,
  renderItem,
}: {
  items: T[];
  emptyText: string;
  renderItem: (item: T) => React.ReactNode;
}) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-gray-500 text-center py-4" data-testid="supervision-empty">
        {emptyText}
      </p>
    );
  }
  return <div className="flex flex-col gap-1">{items.map(renderItem)}</div>;
}

function QueueItem({
  title,
  subtitle,
  severity,
  link,
}: {
  title: string;
  subtitle: string;
  severity: 'critical' | 'warning' | 'info';
  link: string;
}) {
  const colorMap = {
    critical: 'border-l-red-500 bg-red-50/50',
    warning: 'border-l-amber-500 bg-amber-50/50',
    info: 'border-l-blue-500 bg-blue-50/50',
  };

  return (
    <a
      href={link}
      className={cn(
        'block rounded border-l-2 px-2.5 py-2 hover:shadow-sm transition-shadow',
        colorMap[severity],
      )}
      data-testid="supervision-queue-item"
    >
      <p className="text-xs font-medium text-gray-900 truncate">{title}</p>
      <div className="flex items-center gap-1 mt-0.5">
        <p className="text-[11px] text-gray-600 truncate flex-1">{subtitle}</p>
        <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
      </div>
    </a>
  );
}
